/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useState } from "react";
import { graphql, Link } from "gatsby";
import { DocumentContext } from "~context/DocumentContext";
import Footer from "~components/Footer";
import Layout from "~components/Layout";
import ProductGrid from "~components/ProductGrid.jsx";
import ScreenHeight from "~components/ScreenHeight";
import SEO from "~components/SEO";
import ChevronDown from "~components/svg/ChevronDown";
import ChevronUp from "~components/svg/ChevronUp";
import Square from "~components/svg/Square";
import SquareTicked from "~components/svg/SquareTicked";

const CollectionPage = ({ data, location }) => {
  const { device } = useContext(DocumentContext);

  const [expandedFilters, setExpandedFilters] = useState([]);
  const [filters, setFilters] = useState({});
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);
  const [products, setProducts] = useState([]);
  const [sorting, setSorting] = useState(null);

  //

  const cms = data?.sanityCollection;

  const brands = [];
  const features = [];
  const hairNeeds = [];
  const hairTypes = [];
  const productTypes = [];
  const prices = [
    {
      key: `Under $25`,
      min: 0,
      max: 25
    },
    {
      key: `$25 - $50`,
      min: 25,
      max: 50
    },
    {
      key: `$50 - $100`,
      min: 50,
      max: 100
    },
    {
      key: `$100 - $200`,
      min: 100,
      max: 200
    },
    {
      key: `From $200`,
      min: 200,
      max: 1000000
    }
  ];

  //
  // store filterable attributes
  if (data?.allSanityProduct?.edges?.[0]) {
    data.allSanityProduct.edges.forEach(({ node }) => {
      node.collections.forEach(productCollection => {
        if (
          productCollection?.handle &&
          cms?.handle &&
          productCollection.handle === cms.handle
        ) {
          if (node?.brands?.[0]) {
            node.brands.forEach(({ title }) => {
              if (!brands.includes(title)) {
                brands.push(title);
              }
            });
          }

          if (node?.features?.[0]) {
            node.features.forEach(feature => {
              if (!features.includes(feature)) {
                features.push(feature);
              }
            });
          }

          if (node?.hairNeeds?.[0]) {
            node.hairNeeds.forEach(hairNeed => {
              if (!hairNeeds.includes(hairNeed)) {
                hairNeeds.push(hairNeed);
              }
            });
          }

          if (node?.hairTypes?.[0]) {
            node.hairTypes.forEach(hairType => {
              if (!hairTypes.includes(hairType)) {
                hairTypes.push(hairType);
              }
            });
          }

          if (node?.productTypes?.[0]) {
            node.productTypes.forEach(productType => {
              if (!productTypes.includes(productType)) {
                productTypes.push(productType);
              }
            });
          }
        }
      });
    });
  }

  const checkFilterByKey = (key, product) => {
    if (!filters?.[key]?.[0]) {
      return true;
    }

    let filterMatched = false;

    filters[key].forEach(filterValue => {
      if (filterMatched) {
        return;
      }

      filterMatched = product?.[key]?.includes(filterValue);
    });

    return filterMatched;
  };

  const toggleExpander = key => {
    const expanded = JSON.parse(JSON.stringify(expandedFilters));
    const includedIndex = expanded.indexOf(key);

    if (includedIndex === -1) {
      expanded.push(key);
    } else {
      expanded.splice(includedIndex, 1);
    }

    setExpandedFilters(expanded);
  };

  const toggleFilter = (key, value) => {
    const adjustedFilters = JSON.parse(JSON.stringify(filters));

    if (!adjustedFilters?.[key]) {
      adjustedFilters[key] = [];
    }

    const includedIndex = adjustedFilters[key].indexOf(value);

    if (includedIndex === -1) {
      adjustedFilters[key].push(value);
    } else {
      adjustedFilters[key].splice(includedIndex, 1);
    }

    setFilters(adjustedFilters);
  };

  //

  useEffect(() => {
    const loadedProducts = [];

    //
    // product filtering
    if (data?.allSanityProduct?.edges?.[0]) {
      data.allSanityProduct.edges.forEach(({ node }) => {
        node.collections.forEach(productCollection => {
          if (
            productCollection?.handle &&
            cms?.handle &&
            productCollection.handle === cms.handle
          ) {
            let hasBrand = true;
            if (filters?.brands?.[0] && node?.brands?.[0]) {
              hasBrand = false;

              node.brands.forEach(({ title }) => {
                if (hasBrand) {
                  return;
                }

                if (filters.brands.includes(title)) {
                  hasBrand = true;
                }
              });
            }

            const hasFeature = checkFilterByKey(`features`, node);
            const hasHairNeed = checkFilterByKey(`hairNeeds`, node);
            const hasHairType = checkFilterByKey(`hairTypes`, node);
            const hasProductType = checkFilterByKey(`productTypes`, node);

            let hasPrice = true;
            if (filters?.prices?.[0]) {
              hasPrice = false;

              filters.prices.forEach(priceKey => {
                if (hasPrice || !node?.shopifyProduct?.variants?.[0]?.price) {
                  return;
                }

                const price = prices.find(element => element.key === priceKey);
                const productPrice = node.shopifyProduct.variants[0].price;

                hasPrice =
                  productPrice >= price.min && productPrice < price.max;
              });
            }

            if (
              hasBrand &&
              hasFeature &&
              hasHairNeed &&
              hasHairType &&
              hasProductType &&
              hasPrice
            ) {
              loadedProducts.push(node);
            }
          }
        });
      });
    }

    setProducts(loadedProducts);
  }, [filters]);

  //

  return (
    <>
      <SEO
        customTitle={cms?.title}
        customDescription={cms?.seoDescription}
        customKeywords={cms?.seoKeywords}
        path={location.pathname}
      />

      <Layout
        className={`collection-page w-full relative md:pb-12 ${
          device === `desktop` ? `pt-v8` : `pt-24 pb-20`
        } bg-bone`}
      >
        <section className="grid">
          <p
            className={`grid-end-12 hidden md:block ${
              device === `desktop` ? `b2` : `mb-8 caption`
            }`}
          >
            <Link to="/" className="hover-underline">
              Home
            </Link>

            <span> . </span>

            <Link to="/collections" className="hover-underline">
              Collection
            </Link>

            <span> . {cms.title}</span>
          </p>
        </section>

        <section className="w-full relative">
          <div className="grid">
            {device && device === `desktop` && (
              <ScreenHeight className="collection-page__filters animation-appear-right-slow animation-delay-2 grid-end-2 sticky top-0 left-0 overflow-y-scroll pt-v3 pb-v12">
                <div className="w-full h-full relative overflow-y-scroll">
                  <div className="mb-v05 f4">{products.length} Items</div>

                  {/* // */}

                  {hairNeeds?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`hairNeeds`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Hair Need</span>
                          <span>
                            {expandedFilters.includes(`hairNeeds`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`hairNeeds`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {hairNeeds.map(hairNeed => (
                            <li key={hairNeed} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() =>
                                  toggleFilter(`hairNeeds`, hairNeed)
                                }
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.hairNeeds?.includes(hairNeed) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{hairNeed}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {hairTypes?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`hairTypes`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Hair Type</span>
                          <span>
                            {expandedFilters.includes(`hairTypes`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`hairTypes`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {hairTypes.map(hairType => (
                            <li key={hairType} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() =>
                                  toggleFilter(`hairTypes`, hairType)
                                }
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.hairTypes?.includes(hairType) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{hairType}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {productTypes?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`productTypes`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Product Type</span>
                          <span>
                            {expandedFilters.includes(`productTypes`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`productTypes`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {productTypes.map(productType => (
                            <li key={productType} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() =>
                                  toggleFilter(`productTypes`, productType)
                                }
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.productTypes?.includes(
                                    productType
                                  ) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{productType}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {features?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`features`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Choices</span>
                          <span>
                            {expandedFilters.includes(`features`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`features`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {features.map(feature => (
                            <li key={feature} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() =>
                                  toggleFilter(`features`, feature)
                                }
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.features?.includes(feature) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{feature}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {brands?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`brands`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Brand</span>
                          <span>
                            {expandedFilters.includes(`brands`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`brands`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {brands.map(brand => (
                            <li key={brand} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() => toggleFilter(`brands`, brand)}
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.brands?.includes(brand) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{brand}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {prices?.[0] && (
                    <div className="collection-page__filter-group w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`prices`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Price</span>
                          <span>
                            {expandedFilters.includes(`prices`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`prices`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {prices.map(price => (
                            <li key={price.key} className="relative block">
                              <button
                                type="button"
                                className="w-full relative block pt-1 pb-1"
                                onClick={() =>
                                  toggleFilter(`prices`, price.key)
                                }
                              >
                                <div className="w-full relative flex items-center">
                                  {(filters?.prices?.includes(price.key) && (
                                    <SquareTicked className="w-4 mr-v05" />
                                  )) || <Square className="w-4 mr-v05" />}

                                  <span className="caption">{price.key}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="mt-v2 mb-v8 pt-1 pb-1 b2"
                    onClick={() => {
                      setExpandedFilters([]);
                      setFilters({});
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              </ScreenHeight>
            )}

            <div className="grid-end-12 md:grid-end-9">
              <article className="w-full relative flex flex-col items-center justify-center mb-v2 text-center">
                <h1 className="animation-appear-down-slow animation-delay-2 f3">
                  {cms.title}
                </h1>

                <p className="animation-appear-down-slow animation-delay-3 md:w-3/4 relative mt-v1 b1">
                  {cms.description}
                </p>
              </article>

              <div
                className={`animation-appear-down-slow animation-delay-4 w-full relative ${
                  device === `desktop` ? `pr-v05 mb-v2` : `mt-8 mb-4`
                } flex justify-end caption`}
              >
                {device && device !== `desktop` && (
                  <div className="w-full h-10 mr-1 pl-1 relative block border border-clay">
                    <button
                      type="button"
                      className="w-full h-full relative block text-left"
                      onClick={() =>
                        setMobileFiltersExpanded(!mobileFiltersExpanded)
                      }
                    >
                      <span className="block caption">Filters</span>
                    </button>
                  </div>
                )}

                <select
                  className="w-full md:w-32 h-10 md:h-6 ml-1 md:ml-0 pl-1 cursor-pointer bg-bone border-clay border"
                  onChange={e => setSorting(e.currentTarget.value)}
                >
                  <option>Best Sellers</option>
                  <option value="Lowest Price">Lowest Price</option>
                  <option value="Highest Price">Highest Price</option>
                  {/* <option>Top Rated</option> */}
                </select>
              </div>

              {device && device !== `desktop` && mobileFiltersExpanded && (
                <div className="animation-appear-down-slow w-full relative block mb-8">
                  {hairNeeds?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`hairNeeds`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Hair Need</span>
                          <span>
                            {expandedFilters.includes(`hairNeeds`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`hairNeeds`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {hairNeeds.map((hairNeed, hairNeedIndex) => {
                            if (!hairNeed || hairNeed.trim() === ``) {
                              const key = `hair-need-${hairNeedIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`hair-need-${hairNeed}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() =>
                                    toggleFilter(`hairNeeds`, hairNeed)
                                  }
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.hairNeeds?.includes(
                                      hairNeed
                                    ) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">{hairNeed}</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {hairTypes?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`hairTypes`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Hair Type</span>
                          <span>
                            {expandedFilters.includes(`hairTypes`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`hairTypes`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {hairTypes.map((hairType, hairTypeIndex) => {
                            if (!hairType || hairType.trim() === ``) {
                              const key = `hair-type-${hairTypeIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`hair-type-${hairType}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() =>
                                    toggleFilter(`hairTypes`, hairType)
                                  }
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.hairTypes?.includes(
                                      hairType
                                    ) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">{hairType}</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {productTypes?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`productTypes`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Product Type</span>
                          <span>
                            {expandedFilters.includes(`productTypes`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`productTypes`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {productTypes.map((productType, productTypeIndex) => {
                            if (!productType || productType.trim() === ``) {
                              const key = `product-type-${productTypeIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`product-type-${productType}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() =>
                                    toggleFilter(`productTypes`, productType)
                                  }
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.productTypes?.includes(
                                      productType
                                    ) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">
                                      {productType}
                                    </span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {features?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`features`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Choices</span>
                          <span>
                            {expandedFilters.includes(`features`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`features`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {features.map((feature, featureIndex) => {
                            if (!feature || feature.trim() === ``) {
                              const key = `feature-${featureIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`product-type-${feature}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() =>
                                    toggleFilter(`features`, feature)
                                  }
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.features?.includes(feature) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">{feature}</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* // */}

                  {brands?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`brands`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Brand</span>
                          <span>
                            {expandedFilters.includes(`brands`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`brands`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {brands.map((brand, brandIndex) => {
                            if (!brand || brand.trim() === ``) {
                              const key = `brand-${brandIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`brand-${brand}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() => toggleFilter(`brands`, brand)}
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.brands?.includes(brand) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">{brand}</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {prices?.[0] && (
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="w-full relative block pt-v05 pb-v05"
                        onClick={() => toggleExpander(`prices`)}
                      >
                        <div className="w-full relative flex items-center justify-between b2">
                          <span>Price</span>
                          <span>
                            {expandedFilters.includes(`prices`) ? (
                              <ChevronDown className="animation-appear-down w-3" />
                            ) : (
                              <ChevronUp className="animation-appear w-3" />
                            )}
                          </span>
                        </div>
                      </button>

                      {expandedFilters.includes(`prices`) && (
                        <ul className="w-full relative pt-1 pb-2">
                          {prices.map((price, priceIndex) => {
                            if (!price || price.trim() === ``) {
                              const key = `price-${priceIndex}`;

                              return (
                                <React.Fragment key={key}></React.Fragment>
                              );
                            }

                            return (
                              <li
                                key={`price-${price.key}`}
                                className="relative block"
                              >
                                <button
                                  type="button"
                                  className="w-full relative block pt-1 pb-1"
                                  onClick={() =>
                                    toggleFilter(`prices`, price.key)
                                  }
                                >
                                  <div className="w-full relative flex items-center">
                                    {(filters?.prices?.includes(price.key) && (
                                      <SquareTicked className="w-4 mr-v05" />
                                    )) || <Square className="w-4 mr-v05" />}

                                    <span className="caption">{price.key}</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="animation-appear-down-slow animation-delay-5">
                <ProductGrid products={products} sorting={sorting} />
              </div>
            </div>
          </div>
        </section>
      </Layout>

      <Footer />
    </>
  );
};

export default CollectionPage;

export const query = graphql`
  query CollectionPage($id: String!) {
    sanityCollection(id: { eq: $id }) {
      title
      seoDescription
      seoKeywords

      handle
      description
      image {
        asset {
          fluid {
            ...GatsbySanityImageFluid_noBase64
          }
        }
        altText
      }
    }

    allSanityProduct {
      edges {
        node {
          title
          handle
          priority

          image {
            asset {
              fluid {
                ...GatsbySanityImageFluid_noBase64
              }
            }
            altText
          }

          brands {
            title
          }

          collections {
            title
            handle
          }

          hairNeeds
          hairTypes
          productTypes
          features

          shopifyProduct {
            id
            title
            description
            handle
            images {
              originalSrc
            }
            productType
            vendor
            variants {
              id
              title
              image {
                originalSrc
              }
              price
              presentmentPrices {
                edges {
                  node {
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;
